import { useCallback, useEffect, useState } from "react";
import { Download, FileSpreadsheet, LogOut } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

function Stat({ label, value }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="font-mono text-2xl font-extrabold text-[#111827]">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-[0.16em] text-[#6B7280]">{label}</div>
    </div>
  );
}

export default function SocietyAuditorDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/society/audit/dashboard");
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Unable to load audit dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadReport = async (report) => {
    try {
      const res = await api.get("/society/audit/export", { params: { report }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `society-${report}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err, "Could not download report"));
    }
  };

  const openAttachment = async (attachment) => {
    try {
      const res = await api.get(`/society/attachments/${attachment.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatErr(err, "Could not open attachment"));
    }
  };

  const downloadReceipt = async (payment) => {
    try {
      const res = await api.get(`/society/billing/payments/${payment.id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${payment.receiptNumber || "society-receipt"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err, "Could not download receipt"));
    }
  };

  const signOut = async () => {
    await logout();
    window.location.href = "/login";
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] text-sm text-[#6B7280]">Loading audit dashboard...</div>;
  }

  const summary = data?.summary || {};
  const yearEnd = data?.yearEnd || {};
  const counts = data?.counts || {};
  const outstanding = data?.outstanding || {};
  const openingTotal = (data?.openingBalances || []).reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const latestClose = (data?.financialCloses || []).find((row) => row.status === "closed");

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#111827]">
      <header className="border-b border-[#E5E7EB] bg-white px-4 py-4">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">CA / Auditor</div>
            <h1 className="text-2xl font-extrabold">{data?.client?.name || user?.client_name || "Society audit"}</h1>
            <div className="mt-1 text-sm text-[#6B7280]">{user?.name} | Read-only access</div>
          </div>
          <Button variant="outline" onClick={signOut}><LogOut className="mr-2 h-4 w-4" /> Logout</Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-5 px-4 py-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Stat label="Income" value={`₹ ${Number(summary.income || 0).toLocaleString()}`} />
          <Stat label="Expense" value={`₹ ${Number(summary.expense || 0).toLocaleString()}`} />
          <Stat label="Net" value={`₹ ${Number(summary.net || 0).toLocaleString()}`} />
          <Stat label="Bills" value={counts.bills || 0} />
          <Stat label="Outstanding" value={`₹ ${Number(outstanding.totalOutstanding || 0).toLocaleString()}`} />
          <Stat label="Bank unmatched" value={counts.bankUnmatched || 0} />
          <Stat label="WA failed" value={counts.whatsappFailed || 0} />
          <Stat label="Opening" value={`₹ ${Number(openingTotal || 0).toLocaleString()}`} />
          <Stat label="Audit lock" value={latestClose ? `Closed to ${latestClose.toDate}` : "Open"} />
        </div>

        <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
          <div className="mb-4 flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            <h2 className="text-lg font-extrabold">Audit downloads</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {["ledger", "bills", "payments", "outstanding", "bank_reconciliation", "trial_balance", "income_expense", "balance_sheet", "fund_balances", "audit_logs", "whatsapp_logs"].map((report) => (
              <Button key={report} variant="outline" onClick={() => downloadReport(report)}><Download className="mr-2 h-4 w-4" /> {report}</Button>
            ))}
          </div>
        </div>

        <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
          <h2 className="mb-4 text-lg font-extrabold">Year-end summary</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Surplus" value={`₹ ${Number(yearEnd.incomeExpense?.surplus || 0).toLocaleString()}`} />
            <Stat label="Assets" value={`₹ ${Number(yearEnd.balanceSheet?.assets || 0).toLocaleString()}`} />
            <Stat label="Receivable" value={`₹ ${Number(yearEnd.balanceSheet?.outstandingReceivable || 0).toLocaleString()}`} />
            <Stat label="Funds" value={yearEnd.fundBalances?.length || 0} />
          </div>
        </div>

        <div className="grid gap-5 xl:grid-cols-2">
          <section className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <h2 className="mb-4 text-lg font-extrabold">Latest ledger entries</h2>
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Category</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data?.latestEntries || []).map((entry) => (
                  <TableRow key={entry.id}><TableCell>{entry.entryDate}</TableCell><TableCell>{entry.entryType}</TableCell><TableCell>{entry.category || entry.account}</TableCell><TableCell>{entry.entryStatus || "approved"}</TableCell><TableCell className="text-right">₹ {Number(entry.amount || 0).toLocaleString()}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </section>

          <section className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <h2 className="mb-4 text-lg font-extrabold">Latest bills</h2>
            <Table>
              <TableHeader><TableRow><TableHead>Bill</TableHead><TableHead>Flat</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data?.latestBills || []).map((bill) => (
                  <TableRow key={bill.id}><TableCell>{bill.billNumber}</TableCell><TableCell>{bill.wing} {bill.flatNumber}</TableCell><TableCell>{bill.status}</TableCell><TableCell className="text-right">₹ {Number(bill.balanceDue || 0).toLocaleString()}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </section>

          <section className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <h2 className="mb-4 text-lg font-extrabold">Latest receipts</h2>
            <Table>
              <TableHeader><TableRow><TableHead>Receipt</TableHead><TableHead>Owner</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data?.latestPayments || []).filter((payment) => payment.status === "accepted").map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell className="font-mono text-xs">{payment.receiptNumber || "-"}</TableCell>
                    <TableCell>{payment.ownerName || "-"}</TableCell>
                    <TableCell>{payment.status}</TableCell>
                    <TableCell className="text-right">₹ {Number(payment.amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => downloadReceipt(payment)}><Download className="mr-2 h-4 w-4" /> Receipt</Button></TableCell>
                  </TableRow>
                ))}
                {(!(data?.latestPayments || []).some((payment) => payment.status === "accepted")) && <TableRow><TableCell colSpan={5} className="py-8 text-center text-[#6B7280]">No accepted receipts yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </section>

          <section className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <h2 className="mb-4 text-lg font-extrabold">WhatsApp delivery</h2>
            <Table>
              <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Type</TableHead><TableHead>Reference</TableHead><TableHead>Status</TableHead><TableHead>Error</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data?.latestWhatsappLogs || []).map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-xs">{log.createdAt ? new Date(log.createdAt).toLocaleString() : "-"}</TableCell>
                    <TableCell className="capitalize">{log.messageType || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{log.billNumber || log.receiptNumber || log.entityId || "-"}</TableCell>
                    <TableCell>{log.status || "-"}</TableCell>
                    <TableCell className="max-w-[280px] truncate text-xs text-[#6B7280]">{log.error || "-"}</TableCell>
                  </TableRow>
                ))}
                {(!data?.latestWhatsappLogs || data.latestWhatsappLogs.length === 0) && <TableRow><TableCell colSpan={5} className="py-8 text-center text-[#6B7280]">No WhatsApp logs yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </section>

          <section className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <h2 className="mb-4 text-lg font-extrabold">Latest audit trail</h2>
            <Table>
              <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data?.latestAuditLogs || []).map((log) => (
                  <TableRow key={log.id}><TableCell className="text-xs">{log.createdAt ? new Date(log.createdAt).toLocaleString() : "-"}</TableCell><TableCell>{log.actorName || log.actorRole || "-"}</TableCell><TableCell>{log.action}</TableCell><TableCell>{log.entityType}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </section>

          <section className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <h2 className="mb-4 text-lg font-extrabold">Latest attachments</h2>
            <Table>
              <TableHeader><TableRow><TableHead>Uploaded</TableHead><TableHead>Type</TableHead><TableHead>File</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data?.latestAttachments || []).map((attachment) => (
                  <TableRow key={attachment.id}>
                    <TableCell className="text-xs">{attachment.createdAt ? new Date(attachment.createdAt).toLocaleString() : "-"}</TableCell>
                    <TableCell>{attachment.entityType}</TableCell>
                    <TableCell className="max-w-[260px] truncate">{attachment.filename || attachment.label || "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => openAttachment(attachment)}><Download className="mr-2 h-4 w-4" /> Open</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(!data?.latestAttachments || data.latestAttachments.length === 0) && <TableRow><TableCell colSpan={4} className="py-8 text-center text-[#6B7280]">No attachments uploaded yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </section>
        </div>
      </main>
    </div>
  );
}
